import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
    FieldError,
    FieldValues,
    useController,
    useFieldArray,
} from 'react-hook-form';

import {
    IContentTypeValue,
    IExtraCategoryOption,
    IGroupListItemFragment,
    GroupListItemFragmentDoc,
    ExtraCategoryFiltersDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { Loader } from 'common/components/Loader';
import { getExtraCategoryFilterItems } from 'organisation/utils/extraCategory';
import { getSelectedGroupConditionFilters } from 'user/utils/groups';
import { AddButton } from 'common/components/Button/AddButton';
import { IGroupConditionInput, TGroupListItem } from 'user/types';
import { GroupSelectDrawer } from 'user/components/GroupSelectDrawer';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { ListPanel, IListPanelMenuOption } from 'common/components/ListPanel';
import { Divider } from 'common/components/Divider';
import { GroupConditionGroupExtendedListItem } from 'user/forms/Field/GroupConditionFilterField/GroupConditionGroupExtendedListItem';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const GroupConditionExtendedFilterField = <
    TFieldValues extends FieldValues,
>({
    control: formControl,
    name,
}: IProps & TControllerProps<TFieldValues>) => {
    const { showApolloError } = useApolloError();
    const [translate] = useTranslation();
    const client = useApolloClient();

    const {
        data: extraCategoryFiltersData,
        loading: extraCategoryFiltersLoading,
        error: extraCategoryFiltersError,
    } = useQuery(ExtraCategoryFiltersDocument, {
        variables: { contentType: IContentTypeValue.Group },
    });

    useEffect(() => {
        if (extraCategoryFiltersError) {
            showApolloError(extraCategoryFiltersError);
        }
    }, [extraCategoryFiltersError, showApolloError]);
    const [selectedFilterIndex, setSelectedFilterIndex] = useState<
        number | undefined
    >();
    const [selectDrawerOpen, setSelectDrawerOpen] = useState(false);
    const [showOnDeleteAlert, setShowOnDeleteAlert] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<TGroupListItem[]>([]);

    const control = asTypedControl<IGroupConditionInput[]>(name, formControl);

    const {
        fieldState: { error: fieldError },
    } = useController({
        name,
        control,
    });

    const errorMessage = (fieldError?.[name as keyof FieldError] as FieldError)
        ?.message;

    const {
        fields: groupConditionInputs,
        append,
        remove,
        update,
    } = useFieldArray({
        control,
        name,
        keyName: 'key',
    });

    const [minMaxFiltersOn, setMinMaxFiltersOn] = useState<boolean[]>([]);
    const prevFlagsRef = useRef<boolean[]>([]);

    // Sync minMaxFiltersOn state when groupConditionInputs change (e.g. after form reset on edit page).
    // Uses a ref to bail out when the derived flags haven't actually changed,
    // avoiding re-renders from useFieldArray's referential instability.
    useEffect(() => {
        const currentFlags = groupConditionInputs.map(
            (input) =>
                !!input.minMaxFilters?.some(
                    (filter) => filter.min != null || filter.max != null
                )
        );

        if (
            currentFlags.length === prevFlagsRef.current.length &&
            currentFlags.every((flag, i) => flag === prevFlagsRef.current[i])
        ) {
            return;
        }

        prevFlagsRef.current = currentFlags;

        if (!currentFlags.length) {
            setMinMaxFiltersOn([]);

            return;
        }

        setMinMaxFiltersOn((prev) => {
            const next = currentFlags.map(
                (hasMinMax, i) => prev[i] ?? hasMinMax
            );

            if (
                next.length === prev.length &&
                next.every((val, i) => val === prev[i])
            ) {
                return prev;
            }

            return next;
        });
    }, [groupConditionInputs]);

    const { extraCategoriesFilters } = extraCategoryFiltersData || {};

    // Only use choice type filters
    const filteredCategoryFilters = extraCategoriesFilters?.filter(
        (categoryFilter) =>
            categoryFilter.categoryType === IExtraCategoryOption.Choice
    );

    const loading = extraCategoryFiltersLoading;

    // Retrieve group objects from cache based on UIDs stored in the form
    const getGroupsFromCache = useMemo(
        () =>
            (groupIds: string[]): TGroupListItem[] =>
                (groupIds
                    ?.map((groupId) => {
                        try {
                            const group =
                                client.cache.readFragment<IGroupListItemFragment>(
                                    {
                                        id: `Group:${groupId}`,
                                        fragment: GroupListItemFragmentDoc,
                                        fragmentName: 'GroupListItem',
                                    }
                                );

                            if (!group) return null;

                            // Remove isEditable to match TGroupListItem type
                            const { isEditable, ...groupListItem } = group;

                            return groupListItem as TGroupListItem;
                        } catch {
                            return null;
                        }
                    })
                    .filter(Boolean) || []) as TGroupListItem[],
        [client.cache]
    );

    const handleSelectGroup = (group: TGroupListItem) => {
        const newGroups = [...selectedGroups, group].sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        setSelectedGroups(newGroups);
    };

    const handleAddGroups = (selectedGroups: TGroupListItem[]) => {
        // Extract UIDs from selected groups
        const groupIds = selectedGroups.map((group) => group.id);

        // Min/max changes are routed through useFieldArray.update() (via
        // handleMinMaxUpdate), so groupConditionInputs is always up-to-date.
        const existingMinMaxFilters =
            selectedFilterIndex !== undefined && groupConditionInputs?.length
                ? groupConditionInputs[selectedFilterIndex].minMaxFilters || []
                : [];

        const minMaxFilters = selectedGroups.map((group) => {
            const existing = existingMinMaxFilters.find(
                (filter) => filter.groupId === group.id
            );

            return existing ?? { groupId: group.id, min: null, max: null };
        });

        let newGroupConditionInput = {
            groups: groupIds,
            minMaxFilters,
        };

        // If existing group condition is being edited, replace it with new values
        if (selectedFilterIndex !== undefined && groupConditionInputs?.length) {
            newGroupConditionInput = {
                ...groupConditionInputs[selectedFilterIndex],
                ...newGroupConditionInput,
            };

            update(selectedFilterIndex, newGroupConditionInput);
        } else {
            append(newGroupConditionInput);
        }

        setSelectedFilterIndex(undefined);
        setSelectDrawerOpen(false);
    };

    const addGroupCondition = () => {
        // Clear selected groups when adding a new group condition
        setSelectedGroups([]);

        setSelectDrawerOpen(true);
    };

    const addedGroups = useMemo(() => {
        if (
            selectedFilterIndex === undefined ||
            !groupConditionInputs?.length
        ) {
            return [];
        }

        const groupIds = groupConditionInputs[selectedFilterIndex].groups;

        return getGroupsFromCache(groupIds);
    }, [selectedFilterIndex, groupConditionInputs, getGroupsFromCache]);

    const selectedFilters = useMemo(() => {
        if (
            selectedFilterIndex === undefined ||
            !groupConditionInputs?.length
        ) {
            return [];
        }

        const { extraCategoryValues } =
            groupConditionInputs[selectedFilterIndex];

        return getSelectedGroupConditionFilters(
            extraCategoryValues,
            getExtraCategoryFilterItems(filteredCategoryFilters)
        );
    }, [selectedFilterIndex, groupConditionInputs, extraCategoriesFilters]);

    const drawerOpen = selectDrawerOpen || selectedFilterIndex !== undefined;

    const noneOfTheGroupsHaveMinMaxFilters = minMaxFiltersOn.every(
        (minMaxFilter) => !minMaxFilter
    );

    return (
        <Box>
            {loading && (
                <Box sx={{ height: 40, position: 'relative', mb: 2 }}>
                    <Loader />
                </Box>
            )}

            {!loading && (
                <>
                    <AddButton
                        aria-label={translate('groupConditionFilter.addButton')}
                        onClick={addGroupCondition}
                    >
                        {translate('groupConditionFilter.addButton')}
                    </AddButton>

                    {!!errorMessage && (
                        <FormHelperText error sx={{ mt: 2 }}>
                            {errorMessage}
                        </FormHelperText>
                    )}

                    {!!groupConditionInputs?.length && (
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            {groupConditionInputs.map(
                                (
                                    groupConditionInput,
                                    groupConditionInputIndex
                                ) => {
                                    const { groups: groupIds } =
                                        groupConditionInput;

                                    // Retrieve group objects from cache for this group condition
                                    const groups = getGroupsFromCache(groupIds);

                                    // If there is a group that the user does not have permission to update (is group manager of),
                                    // the user cannot delete the group condition
                                    const canDelete = !groups.some(
                                        ({ permissions }) => {
                                            const groupPermissions = permissions
                                                ? JSON.parse(permissions)
                                                : undefined;

                                            return (
                                                !groupPermissions ||
                                                !groupPermissions.canUpdate
                                            );
                                        }
                                    );

                                    const currentGroupConditionHasMinMaxFilters =
                                        minMaxFiltersOn[
                                            groupConditionInputIndex
                                        ];

                                    const menuOptions: IListPanelMenuOption[] =
                                        [
                                            {
                                                label: translate('edit'),
                                                onClick: () => {
                                                    const groupIds =
                                                        groupConditionInputs[
                                                            groupConditionInputIndex
                                                        ].groups;

                                                    // Retrieve group objects from cache and set them for editing
                                                    if (groupIds) {
                                                        const retrievedGroups =
                                                            getGroupsFromCache(
                                                                groupIds
                                                            );

                                                        setSelectedGroups(
                                                            retrievedGroups
                                                        );
                                                    }

                                                    setSelectedFilterIndex(
                                                        groupConditionInputIndex
                                                    );
                                                },
                                            },
                                            {
                                                label: translate('delete'),
                                                onClick: () => {
                                                    if (canDelete) {
                                                        remove(
                                                            groupConditionInputIndex
                                                        );

                                                        return;
                                                    }

                                                    setShowOnDeleteAlert(true);
                                                },
                                            },
                                        ];

                                    // Only one group can have min/max filters at the same time, show the toggle when:
                                    // - None of the groups have min/max filters, so every group condition has the toggle
                                    // - The current group condition has min/max filters on, only current group condition has the toggle
                                    if (
                                        noneOfTheGroupsHaveMinMaxFilters ||
                                        currentGroupConditionHasMinMaxFilters
                                    ) {
                                        menuOptions.push({
                                            label: translate(
                                                `groupConditionFilter.${minMaxFiltersOn[groupConditionInputIndex] ? 'minMaxFiltersOff' : 'minMaxFiltersOn'}`
                                            ),
                                            onClick: () => {
                                                const newMinMaxFiltersOn = [
                                                    ...minMaxFiltersOn,
                                                ];

                                                const currentMinMaxFiltersOn =
                                                    newMinMaxFiltersOn[
                                                        groupConditionInputIndex
                                                    ];

                                                // Toggle the min/max filters on/off
                                                newMinMaxFiltersOn[
                                                    groupConditionInputIndex
                                                ] = !currentMinMaxFiltersOn;

                                                setMinMaxFiltersOn(
                                                    newMinMaxFiltersOn
                                                );

                                                // Read fresh values from the field array to avoid stale closures
                                                const currentInput =
                                                    groupConditionInputs[
                                                        groupConditionInputIndex
                                                    ];
                                                const currentGroupIds =
                                                    currentInput.groups;

                                                // If min/max filters are turned on, set the min/max filters to empty for all groups
                                                if (
                                                    newMinMaxFiltersOn[
                                                        groupConditionInputIndex
                                                    ]
                                                ) {
                                                    update(
                                                        groupConditionInputIndex,
                                                        {
                                                            ...currentInput,
                                                            minMaxFilters:
                                                                currentGroupIds.map(
                                                                    (
                                                                        groupId
                                                                    ) => ({
                                                                        groupId,
                                                                        min: null,
                                                                        max: null,
                                                                    })
                                                                ),
                                                        }
                                                    );
                                                }

                                                // If min/max filters are turned off, clear the min/max filters
                                                if (
                                                    !newMinMaxFiltersOn[
                                                        groupConditionInputIndex
                                                    ]
                                                ) {
                                                    update(
                                                        groupConditionInputIndex,
                                                        {
                                                            ...currentInput,
                                                            minMaxFilters: [],
                                                        }
                                                    );
                                                }
                                            },
                                        });
                                    }

                                    return (
                                        <Box key={groupConditionInput.key}>
                                            <ListPanel
                                                header={
                                                    <Typography fontWeight="bold">
                                                        {translate(
                                                            'groupConditionFilter.groupConditionHeading'
                                                        )}
                                                    </Typography>
                                                }
                                                menuOptions={menuOptions}
                                            >
                                                {groups.map(
                                                    (group, groupIndex) => (
                                                        <Box key={group.id}>
                                                            <GroupConditionGroupExtendedListItem
                                                                group={group}
                                                                groupConditionInput={
                                                                    groupConditionInput
                                                                }
                                                                groupConditionInputIndex={
                                                                    groupConditionInputIndex
                                                                }
                                                                showMinMaxFilters={
                                                                    minMaxFiltersOn[
                                                                        groupConditionInputIndex
                                                                    ]
                                                                }
                                                                onUpdate={
                                                                    update
                                                                }
                                                            />

                                                            {groupIndex <
                                                                groups.length -
                                                                    1 && (
                                                                <Divider
                                                                    sx={{
                                                                        my: 1,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </ListPanel>

                                            {groupConditionInputIndex <
                                                groupConditionInputs.length -
                                                    1 && (
                                                <Divider
                                                    sx={{ mt: 2 }}
                                                    variant="dashed"
                                                >
                                                    <Typography>
                                                        {translate(
                                                            'groupConditionFilter.conditionAnd'
                                                        )}
                                                    </Typography>
                                                </Divider>
                                            )}
                                        </Box>
                                    );
                                }
                            )}
                        </Stack>
                    )}

                    <AlertDialog
                        actions={
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setShowOnDeleteAlert(false);
                                }}
                            >
                                {translate('close')}
                            </Button>
                        }
                        open={showOnDeleteAlert}
                        title={translate(
                            'offerEventForm.cantDeleteGroupCondition.title'
                        )}
                    >
                        <DialogContentText color="text.primary" variant="body2">
                            {translate(
                                'offerEventForm.cantDeleteGroupCondition.text'
                            )}
                        </DialogContentText>
                    </AlertDialog>

                    <GroupSelectDrawer
                        addedGroups={addedGroups}
                        initialSelectedFilters={selectedFilters}
                        open={drawerOpen}
                        selectedGroups={selectedGroups}
                        setSelectedGroups={setSelectedGroups}
                        onAddGroups={(groups) => handleAddGroups(groups || [])}
                        onClose={() => {
                            setSelectedFilterIndex(undefined);
                            setSelectDrawerOpen(false);
                        }}
                        onRemoveGroup={(group) => {
                            setSelectedGroups(
                                selectedGroups.filter(
                                    (selectedGroup) =>
                                        selectedGroup.id !== group.id
                                )
                            );
                        }}
                        onSelectGroup={handleSelectGroup}
                    />
                </>
            )}
        </Box>
    );
};
